import { BlumeInvoiceCustomFieldsPanel } from './BlumeInvoiceCustomFieldsPanel';
import { useEffect, useMemo, useState } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Save, Settings2 } from 'lucide-react';

import {
  getBlumeInvoiceSettings,
  saveBlumeInvoiceSettings,
} from '../../features/integrations/blume/api';

import './BlumeInvoiceSettingsPanel.scss';

function centsInputToInteger(dollars: string): number | null {
  const trimmed = dollars.trim();

  if (!trimmed) {
    return null;
  }

  const value = Number(trimmed);

  if (!Number.isFinite(value) || value < 0) {
    return null;
  }

  return Math.round(value * 100);
}

function centsToInput(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) {
    return '';
  }

  return (cents / 100).toFixed(2);
}

export function BlumeInvoiceSettingsPanel() {
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['blume-invoice-settings'],

    queryFn: getBlumeInvoiceSettings,
  });

  const [fuelServiceId, setFuelServiceId] = useState<string | null>(null);

  const [highwayServiceId, setHighwayServiceId] = useState<string | null>(null);

  const [directShipServiceId, setDirectShipServiceId] = useState('');
  const [deliveryServiceId, setDeliveryServiceId] = useState('');

  const [fuelDefaultRate, setFuelDefaultRate] = useState('');

  const [highwayDefaultRate, setHighwayDefaultRate] = useState('');

  const [initializedKey, setInitializedKey] = useState('');

  const dataKey = useMemo(
    () =>
      data
        ? [
            data.settings.fuelSurcharge?.updatedAt ?? 'fuel-none',

            data.settings.highwayUseFee?.updatedAt ?? 'highway-none',

            data.settings.directShipFee?.updatedAt ?? 'direct-none',
            data.settings.deliveryFee?.updatedAt ?? 'delivery-none',
          ].join('|')
        : '',
    [data]
  );

  /*
   * Populate local form state once for each freshly-loaded settings object.
   *
   * Keeping this in an effect means user edits are not overwritten by unrelated
   * rerenders and avoids setState-during-render.
   */
  useEffect(() => {
    if (!data || !dataKey || initializedKey === dataKey) {
      return;
    }

    setInitializedKey(dataKey);

    setFuelServiceId(data.settings.fuelSurcharge?.quickBooksItemId ?? '');

    setHighwayServiceId(data.settings.highwayUseFee?.quickBooksItemId ?? '');

    setDirectShipServiceId(data.settings.directShipFee?.quickBooksItemId ?? '');
    setDeliveryServiceId(data.settings.deliveryFee?.quickBooksItemId ?? '');

    setFuelDefaultRate(centsToInput(data.settings.fuelSurcharge?.defaultUnitPriceCents));

    setHighwayDefaultRate(centsToInput(data.settings.highwayUseFee?.defaultUnitPriceCents));
  }, [data, dataKey, initializedKey]);

  const saveMutation = useMutation({
    mutationFn: () =>
      saveBlumeInvoiceSettings({
        fuelSurcharge: {
          quickBooksItemId: fuelServiceId || null,

          defaultUnitPriceCents: centsInputToInteger(fuelDefaultRate),
        },

        highwayUseFee: {
          quickBooksItemId: highwayServiceId || null,

          defaultUnitPriceCents: centsInputToInteger(highwayDefaultRate),
        },

        directShipFee: {
          quickBooksItemId: directShipServiceId || null,
          defaultUnitPriceCents: null,
        },
        deliveryFee: { quickBooksItemId: deliveryServiceId || null, defaultUnitPriceCents: null },
      }),

    onSuccess: async (next) => {
      queryClient.setQueryData(['blume-invoice-settings'], next);

      setInitializedKey('');

      await queryClient.invalidateQueries({
        queryKey: ['blume-invoice-settings'],
      });
    },
  });

  if (isLoading) {
    return <div className="blume-invoice-settings">Loading invoice settings…</div>;
  }

  if (error || !data) {
    return (
      <div className="blume-invoice-settings blume-invoice-settings--error">
        Unable to load invoice service settings.
      </div>
    );
  }

  const services = data.serviceOptions;

  return (
    <section className="blume-invoice-settings">
      <div className="blume-invoice-settings__header">
        <div>
          <div className="blume-invoice-settings__eyebrow">
            <Settings2 size={15} />
            QuickBooks invoice services
          </div>

          <h3>Service mappings</h3>

          <p>
            Map recurring BMP invoice charges to existing QuickBooks Service items. These mappings
            provide defaults; the invoice review screen can still add or override service lines
            before creation.
          </p>
        </div>
      </div>

      <div className="blume-invoice-settings__grid">
        <div className="blume-invoice-settings__card">
          <label>Fuel surcharge service</label>

          <select
            value={fuelServiceId ?? ''}
            onChange={(event) => setFuelServiceId(event.target.value)}
          >
            <option value="">Not configured</option>

            {services.map((service) => (
              <option key={service.quickBooksItemId} value={service.quickBooksItemId}>
                {service.fullyQualifiedName ?? service.name}
              </option>
            ))}
          </select>

          <label>Default rate ($, optional)</label>

          <input
            type="number"
            min="0"
            step="0.01"
            value={fuelDefaultRate}
            onChange={(event) => setFuelDefaultRate(event.target.value)}
            placeholder="Calculated from BMP order when available"
          />

          <small>
            BMP already stores fuel-surcharge pricing data. The default rate is only a
            fallback/manual proposal, not the authoritative order amount.
          </small>
        </div>

        <div className="blume-invoice-settings__card">
          <label>Highway use fee service</label>

          <select
            value={highwayServiceId ?? ''}
            onChange={(event) => setHighwayServiceId(event.target.value)}
          >
            <option value="">Not configured</option>

            {services.map((service) => (
              <option key={service.quickBooksItemId} value={service.quickBooksItemId}>
                {service.fullyQualifiedName ?? service.name}
              </option>
            ))}
          </select>

          <label>Default rate ($, optional)</label>

          <input
            type="number"
            min="0"
            step="0.01"
            value={highwayDefaultRate}
            onChange={(event) => setHighwayDefaultRate(event.target.value)}
            placeholder="Example: 0.50"
          />

          <small>
            BMP does not currently calculate a highway-use fee. This rate should therefore be
            treated as a proposed value that the admin may override on the invoice.
          </small>
        </div>
        <div className="blume-invoice-settings__card">
          <label htmlFor="blume-delivery-service">Delivery service (per ton)</label>
          <select
            id="blume-delivery-service"
            value={deliveryServiceId}
            onChange={(event) => setDeliveryServiceId(event.target.value)}
          >
            <option value="">Not configured</option>
            {services.map((service) => (
              <option key={service.quickBooksItemId} value={service.quickBooksItemId}>
                {service.fullyQualifiedName ?? service.name}
              </option>
            ))}
          </select>
          <small>
            BMP uses the saved deliveryFeeSnapshot per ton: QBO Qty equals load tonnage, QBO Rate
            equals saved delivery fee per ton. This is separate from the flat Direct Ship Fee (Qty
            1) and from delivery fuel surcharge.
          </small>
        </div>
        <div className="blume-invoice-settings__card">
          <label htmlFor="blume-direct-ship-service">Direct ship fee service</label>
          <select
            id="blume-direct-ship-service"
            value={directShipServiceId}
            onChange={(event) => setDirectShipServiceId(event.target.value)}
          >
            <option value="">Not configured</option>
            {services.map((service) => (
              <option key={service.quickBooksItemId} value={service.quickBooksItemId}>
                {service.fullyQualifiedName ?? service.name}
              </option>
            ))}
          </select>
          <small>
            BMP uses each load’s saved carrier/direct-ship amount as a flat Service charge (Qty 1).
            It does not apply the QuickBooks item’s default rate or add the fee twice.
          </small>
        </div>
      </div>

      {saveMutation.error && (
        <div className="blume-invoice-settings__error">{(saveMutation.error as Error).message}</div>
      )}

      {saveMutation.isSuccess && (
        <div className="blume-invoice-settings__success">Invoice service settings saved.</div>
      )}

      <div className="blume-invoice-settings__actions">
        <button
          type="button"
          className="btn btn--primary"
          disabled={saveMutation.isPending}
          onClick={() => saveMutation.mutate()}
        >
          <Save size={16} />

          {saveMutation.isPending ? 'Saving…' : 'Save invoice settings'}
        </button>
      </div>
      <BlumeInvoiceCustomFieldsPanel />
    </section>
  );
}
